import { useEffect, useState } from "react";

function read() {
  const vv = window.visualViewport;
  const height = vv?.height ?? window.innerHeight;
  const offsetTop = vv?.offsetTop ?? 0;
  const keyboardInset = Math.max(0, window.innerHeight - height - offsetTop);
  return { height, offsetTop, keyboardInset };
}

/**
 * Tracks the visual viewport (height, offsetTop, keyboard inset). iOS Safari
 * never resizes the layout viewport for the keyboard (it ignores
 * interactive-widget=resizes-content) and fires visualViewport events
 * unreliably during keyboard animations — so on top of event listeners we
 * poll via rAF for ~1.2s after any focus change or viewport event to always
 * settle on correct values.
 */
export function useVisualViewport() {
  const [vp, setVp] = useState(() =>
    typeof window === "undefined" ? { height: 0, offsetTop: 0, keyboardInset: 0 } : read(),
  );

  useEffect(() => {
    let raf = 0;
    let pollUntil = 0;
    const update = () =>
      setVp((prev) => {
        const next = read();
        return prev.height === next.height &&
          prev.offsetTop === next.offsetTop &&
          prev.keyboardInset === next.keyboardInset
          ? prev
          : next;
      });
    const poll = () => {
      update();
      if (performance.now() < pollUntil) raf = requestAnimationFrame(poll);
    };
    const kick = () => {
      pollUntil = performance.now() + 1200;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(poll);
    };
    const vv = window.visualViewport;
    vv?.addEventListener("resize", kick);
    vv?.addEventListener("scroll", kick);
    window.addEventListener("resize", kick);
    window.addEventListener("focusin", kick);
    window.addEventListener("focusout", kick);
    window.addEventListener("orientationchange", kick);
    update();
    return () => {
      cancelAnimationFrame(raf);
      vv?.removeEventListener("resize", kick);
      vv?.removeEventListener("scroll", kick);
      window.removeEventListener("resize", kick);
      window.removeEventListener("focusin", kick);
      window.removeEventListener("focusout", kick);
      window.removeEventListener("orientationchange", kick);
    };
  }, []);

  return vp;
}
